/**
 * Comparer Hook
 * Provides utility functions for adding items to comparer
 */

import { useCallback } from 'react';
import {
  useComparerStore,
  type ComparerItemType,
} from '../store/comparer-store';
import {
  estimateBase64ByteSize,
  parseBinaryBody,
} from '../electron-shared/utils/binary-body';
import { normalizeHttpVersionNumber } from '@terra/shared-utils';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from '../utils/formatHttpForPanel';
import { useMainContentTabs } from '../store/layoutStore';

type RequestLike = {
  id: string;
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body: string;
  httpVersion?: string;
  timestamp: Date | string | number;
  source?: string;
};

type ResponseLike = RequestLike & {
  status?: number;
  statusMessage?: string;
  responseHeaders?: Record<string, string | string[]>;
  responseRawHeaders?: string[];
  responseBody?: string;
  responseHttpVersion?: string;
};

export function useComparer() {
  const { addItem } = useComparerStore();
  const { openTab, setActiveTab, activeTabId } = useMainContentTabs();

  const addRequestToComparer = useCallback(
    (
      request: RequestLike | ResponseLike,
      label?: string,
      type: 'request' | 'response' = 'request',
    ) => {
      let content: string;
      let itemLabel = label;
      const itemType: ComparerItemType = type;

      // Extract metadata
      let metadata: {
        method?: string;
        url?: string;
        status?: number;
        timestamp?: Date;
      } = {};

      try {
        metadata = {
          method: request.method,
          url: request.url,
          timestamp:
            request.timestamp instanceof Date
              ? request.timestamp
              : new Date(request.timestamp),
        };
      } catch {
        // Ignore metadata extraction errors
      }

      if (type === 'request') {
        const requestBody = request.body ?? '';
        const parsedRequestBody = parseBinaryBody(requestBody);

        content = formatHttpRequestRaw({
          method: request.method,
          url: request.url,
          headers: request.headers || {},
          rawHeaders: request.rawHeaders,
          body:
            parsedRequestBody.bodyEncoding === 'base64'
              ? `[Binary content: ${estimateBase64ByteSize(parsedRequestBody.body)} bytes]`
              : requestBody,
          httpVersion: normalizeHttpVersionNumber(request.httpVersion),
        });
      } else {
        const response = request as ResponseLike;
        const status = response.status || 200;
        const responseBody = response.responseBody ?? '';
        const parsedResponseBody = parseBinaryBody(responseBody);

        content = formatHttpResponseRaw({
          status,
          statusMessage: response.statusMessage ?? '',
          headers: response.responseHeaders || {},
          rawHeaders: response.responseRawHeaders,
          body:
            parsedResponseBody.bodyEncoding === 'base64'
              ? `[Binary content: ${estimateBase64ByteSize(parsedResponseBody.body)} bytes]`
              : responseBody,
          httpVersion: normalizeHttpVersionNumber(
            response.responseHttpVersion || response.httpVersion,
          ),
        });

        metadata.status = status;
      }

      if (!itemLabel) {
        try {
          const url = new URL(request.url);
          const timestamp =
            request.timestamp instanceof Date
              ? request.timestamp
              : new Date(request.timestamp);
          const typeLabel = type === 'request' ? 'Request' : 'Response';

          itemLabel = `${typeLabel} - ${request.method} ${url.pathname} - ${timestamp.toLocaleTimeString()}`;
        } catch {
          const timestamp =
            request.timestamp instanceof Date
              ? request.timestamp
              : new Date(request.timestamp);
          const typeLabel = type === 'request' ? 'Request' : 'Response';

          itemLabel = `${typeLabel} - ${request.method} ${request.url} - ${timestamp.toLocaleTimeString()}`;
        }
      }

      addItem({
        id: `${request.id}-${type}-${Date.now()}`,
        label: itemLabel,
        content,
        type: itemType,
        metadata,
      });

      // Get updated items count after adding
      const currentItems = useComparerStore.getState().items;
      const previousActiveTabId = activeTabId;

      // Open comparer tab (creates if doesn't exist, or activates if exists)
      openTab({
        id: 'comparer',
        title: 'Comparer',
        type: 'comparer',
      });

      // Only activate the comparer tab when we have 2+ items (ready to compare)
      // You can send multiple items without
      // switching tabs, and it only switches when ready to compare
      if (currentItems.length >= 2) {
        // Keep comparer tab active (it was just activated by openTab)
        // This means we're ready to compare!
      } else {
        // If only 1 item, switch back to previous tab (user stays on current tab)
        // This allows them to continue selecting items without interruption
        if (previousActiveTabId && previousActiveTabId !== 'comparer') {
          setActiveTab(previousActiveTabId);
        }
      }
    },
    [addItem, openTab, setActiveTab, activeTabId],
  );

  const addTextToComparer = useCallback(
    (text: string, label: string) => {
      addItem({
        id: `text-${Date.now()}`,
        label,
        content: text,
        type: 'text',
      });

      // Open comparer tab
      openTab({
        id: 'comparer',
        title: 'Comparer',
        type: 'comparer',
      });
    },
    [addItem, openTab],
  );

  return {
    addRequestToComparer,
    addTextToComparer,
  };
}
